// skipcq: JS-W1028
import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  formatIngestionRunCompletedAt,
  formatIngestionRunEnumLabel,
  INGESTION_RUN_FILTER_ALL,
} from '../../utils/brewery/beerIngestionRunFilters';
import {
  breweryRunChangesLabel,
  breweryRunListActionLabel,
} from '../../utils/brewery/beerIngestionReview';

function ingestionRunStatusChipColor(status) {
  if (status === 'success') {
    return 'success';
  }
  if (status === 'partial') {
    return 'warning';
  }
  if (status === 'failure') {
    return 'error';
  }
  if (status === 'running') {
    return 'info';
  }
  return 'default';
}

function formatRunErrorTooltip(run) {
  const message = typeof run.errorMessage === 'string' ? run.errorMessage.trim() : '';
  if (!message) {
    return '';
  }
  const stageLabel = run.lastCompletedStage
    ? formatIngestionRunEnumLabel(run.lastCompletedStage)
    : '';
  return stageLabel ? `${stageLabel}: ${message}` : message;
}

function RunStatusChip({ run }) {
  const errorTooltip = formatRunErrorTooltip(run);
  const chipLabel = formatIngestionRunEnumLabel(run.status);
  const chip = (
    <Chip
      size="small"
      color={ingestionRunStatusChipColor(run.status)}
      label={chipLabel}
    />
  );
  if (!errorTooltip) {
    return chip;
  }
  return (
    <Tooltip title={errorTooltip} arrow describeChild>
      <Chip
        size="small"
        component="button"
        type="button"
        color={ingestionRunStatusChipColor(run.status)}
        label={chipLabel}
        sx={{ cursor: 'default' }}
      />
    </Tooltip>
  );
}

RunStatusChip.propTypes = {
  run: PropTypes.object.isRequired,
};

function TruncatedSourceCell({ sourceUrl = '' }) {
  const display = typeof sourceUrl === 'string' && sourceUrl.trim()
    ? sourceUrl.trim()
    : '—';
  return (
    <Typography
      variant="body2"
      title={display}
      sx={{
        maxWidth: 180,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {display}
    </Typography>
  );
}

TruncatedSourceCell.propTypes = {
  sourceUrl: PropTypes.string,
};

function IngestionRunFilterSelect({
  filterId,
  label,
  value,
  options,
  minWidth,
  onChange,
}) {
  return (
    <FormControl size="small" sx={{ minWidth }}>
      <InputLabel id={`${filterId}-label`}>{label}</InputLabel>
      <Select
        labelId={`${filterId}-label`}
        id={filterId}
        value={value}
        label={label}
        onChange={onChange}
      >
        <MenuItem value={INGESTION_RUN_FILTER_ALL}>All</MenuItem>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {formatIngestionRunEnumLabel(option)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

IngestionRunFilterSelect.propTypes = {
  filterId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  minWidth: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

export function IngestionRunsFilterBar({
  statusFilter,
  triggerFilter,
  sourceTypeFilter,
  runKindFilter,
  statusOptions,
  triggerOptions,
  sourceTypeOptions,
  runKindOptions,
  onStatusChange,
  onTriggerChange,
  onSourceTypeChange,
  onRunKindChange,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        alignItems: 'center',
        mb: 2,
      }}
    >
      <IngestionRunFilterSelect
        filterId="ingestion-run-kind-filter"
        label="Run kind"
        value={runKindFilter}
        options={runKindOptions}
        minWidth={140}
        onChange={onRunKindChange}
      />
      <IngestionRunFilterSelect
        filterId="ingestion-run-status-filter"
        label="Status"
        value={statusFilter}
        options={statusOptions}
        minWidth={140}
        onChange={onStatusChange}
      />
      <IngestionRunFilterSelect
        filterId="ingestion-run-trigger-filter"
        label="Trigger"
        value={triggerFilter}
        options={triggerOptions}
        minWidth={140}
        onChange={onTriggerChange}
      />
      <IngestionRunFilterSelect
        filterId="ingestion-run-source-type-filter"
        label="Source type"
        value={sourceTypeFilter}
        options={sourceTypeOptions}
        minWidth={160}
        onChange={onSourceTypeChange}
      />
    </Box>
  );
}

IngestionRunsFilterBar.propTypes = {
  statusFilter: PropTypes.string.isRequired,
  triggerFilter: PropTypes.string.isRequired,
  sourceTypeFilter: PropTypes.string.isRequired,
  runKindFilter: PropTypes.string.isRequired,
  statusOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  triggerOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  sourceTypeOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  runKindOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onTriggerChange: PropTypes.func.isRequired,
  onSourceTypeChange: PropTypes.func.isRequired,
  onRunKindChange: PropTypes.func.isRequired,
};

function BeerIngestionRunTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell>Completed</TableCell>
        <TableCell>Kind</TableCell>
        <TableCell>Status</TableCell>
        <TableCell>Trigger</TableCell>
        <TableCell>Source type</TableCell>
        <TableCell>Source</TableCell>
        <TableCell>User</TableCell>
        <TableCell>Changes</TableCell>
        <TableCell>Action</TableCell>
      </TableRow>
    </TableHead>
  );
}

export function BeerIngestionRunRow({
  run,
  userName = '',
  onViewRun,
  viewingRunId = '',
}) {
  const displayUser = userName || run.userId || '—';
  const isViewing = viewingRunId === run.id;
  const actionLabel = breweryRunListActionLabel(run);
  const handleActionClick = useCallback(() => {
    onViewRun(run);
  }, [onViewRun, run]);

  return (
    <TableRow hover>
      <TableCell>
        {run.completedAt
          ? formatIngestionRunCompletedAt(run.completedAt)
          : 'In progress'}
      </TableCell>
      <TableCell>
        <Chip size="small" label={formatIngestionRunEnumLabel(run.runKind || 'import')} />
      </TableCell>
      <TableCell>
        <RunStatusChip run={run} />
      </TableCell>
      <TableCell>{formatIngestionRunEnumLabel(run.trigger)}</TableCell>
      <TableCell>{formatIngestionRunEnumLabel(run.sourceType)}</TableCell>
      <TableCell>
        <TruncatedSourceCell sourceUrl={run.sourceUrl} />
      </TableCell>
      <TableCell>{displayUser}</TableCell>
      <TableCell>{breweryRunChangesLabel(run)}</TableCell>
      <TableCell>
        <Button
          size="small"
          onClick={handleActionClick}
          disabled={isViewing}
        >
          {actionLabel}
        </Button>
      </TableCell>
    </TableRow>
  );
}

BeerIngestionRunRow.propTypes = {
  run: PropTypes.object.isRequired,
  userName: PropTypes.string,
  onViewRun: PropTypes.func.isRequired,
  viewingRunId: PropTypes.string,
};

export function BeerIngestionRunListPanel({
  runs,
  userNamesById = {},
  loading = false,
  error = null,
  emptyMessage,
  onViewRun,
  viewingRunId = '',
}) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{String(error)}</Alert>;
  }

  if (runs.length === 0) {
    return (
      <Typography color="text.secondary">
        {emptyMessage || 'No ingestion runs for this brewery.'}
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader aria-label="Beer ingestion runs">
        <BeerIngestionRunTableHead />
        <TableBody>
          {runs.map((run) => (
            <BeerIngestionRunRow
              key={run.id}
              run={run}
              userName={userNamesById[run.userId]}
              onViewRun={onViewRun}
              viewingRunId={viewingRunId}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

BeerIngestionRunListPanel.propTypes = {
  runs: PropTypes.arrayOf(PropTypes.object).isRequired,
  userNamesById: PropTypes.objectOf(PropTypes.string),
  loading: PropTypes.bool,
  error: PropTypes.string,
  emptyMessage: PropTypes.string,
  onViewRun: PropTypes.func.isRequired,
  viewingRunId: PropTypes.string,
};
